import { useDelayedTooltip } from "@/hooks/useDelayedTooltip";
import type { PInfoMedia } from "@/components/PInfo/PInfo";
import { PRESS_OWNER_ATTR } from "@/lib/a11y/dismissOwner";

type HoverInfoProps = {
  /** Hint shown in a PInfo bubble after a short hover. No bubble if empty. */
  message?: string;
  /** Hold the bubble back — e.g. the trigger's own popup is open, so the hint
      would just overlap what it was there to announce. */
  suppressed?: boolean;
  /**
   * A tap opens the bubble too (and a second tap or a tap elsewhere closes
   * it). Only for a trigger that is NOTHING but the hint — an ⓘ button: the
   * click then never reaches what is around it. See `useDelayedTooltip`.
   */
  tapToOpen?: boolean;
  /** A small picture above the sentence (see PInfo). */
  media?: PInfoMedia;
  className?: string;
  onClick?: () => void;
  children: React.ReactNode;
};

/**
 * Wraps a trigger (usually a bare icon) in an inline span that shows a delayed
 * {@link PInfo} hint on hover. Use this in list/`map` contexts where calling the
 * `useDelayedTooltip` hook directly isn't possible — the hook runs inside this
 * component instead.
 */
export const HoverInfo: React.FC<HoverInfoProps> = ({
  message,
  suppressed = false,
  tapToOpen = false,
  media,
  className,
  onClick,
  children,
}) => {
  const { ref, onMouseEnter, onMouseLeave, onFocus, onBlur, tooltip, tap } =
    useDelayedTooltip<HTMLSpanElement>(message, undefined, suppressed, { tapToOpen, media });

  if (tap) {
    return (
      <span
        ref={ref}
        className={className}
        onClick={(e) => {
          onClick?.();
          tap.onClick(e);
        }}
        // Pointer events, not mouse events: only they say which device it is,
        // and a finger's synthetic mouseenter must not start the hover timer.
        onPointerEnter={tap.onPointerEnter}
        onPointerLeave={tap.onPointerLeave}
        onPointerDown={tap.onPointerDown}
        onPointerCancel={tap.onPointerCancel}
        onFocus={onFocus}
        onBlur={onBlur}
        {...(tap.pressOwner ? { [PRESS_OWNER_ATTR]: "true" } : {})}
      >
        {children}
        {tooltip}
      </span>
    );
  }

  return (
    <span
      ref={ref}
      className={className}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      // Focus events from the wrapped trigger (a real button) bubble up here,
      // so keyboard users get the same delayed hint as hover.
      onFocus={onFocus}
      onBlur={onBlur}
    >
      {children}
      {tooltip}
    </span>
  );
};
